/**
 * RangeProductTile (KINDW-99 / FSD PLP-03 + design review additions)
 *
 * Tile used by the Range Detail PLP grid. Renders the full PLP tile chrome:
 *   - product image (carousel when ≥2 images, single image otherwise) — sourced via
 *     `getMasterTileImages` so the tile and the PDP main image use the same primitive
 *     (PIM-first via `c_processedImageGroups`, Cylindo as fallback)
 *   - "Sale" badge top-left when `priceData.isOnSale` (PLP-08: list price > sale price)
 *   - "New" badge top-left when `c_newArrivalDate` lands in the past 30 days; Sale + New sit
 *     side by side in the badge row when both apply
 *   - cover swatches overlaid inside the hero image (`bottom` offset from tile frame — see theme)
 *   - product name
 *   - shortDescription subtitle
 *   - sale: "Sale price from" then "Full price from" on the next line (no strikethrough)
 *   - Bazaarvoice star rating below price on PLP tiles (`showBazaarvoiceRating`)
 *   - promo callout below BV (or below price if no BV) on Range PLP lazy grid (`showPromoCallout`)
 *
 * Image behaviour (centralized in `ProductTileImageGallery` / `product-tile-image-behavior.js`):
 *   - <= 2 images: desktop hover swap; mobile grid = swipe + loop; mobile carousel = static hero
 *   - > 2 grid: swipe + loop; desktop arrows on hover; mobile swipe only
 *   - > 2 carousel slide: loop + no swipe; desktop arrows on hover; mobile arrows visible
 *
 * Swatches stay *display-only* on the hero (`pointer-events: none` overlay); taps hit the carousel
 * or image area underneath. PDP handles real selection. The `aria-label` on the swatch wrapper announces the cover
 * count to screen readers; individual swatches expose tooltips via `title`.
 *
 * Click behaviour: whole tile is wrapped in a single `<Link>` to the PDP. The Swiper navigation
 * arrows live in `.swiper-navigation-pagination`; we stop click propagation on those so they don't
 * trigger the link. Mirrors the proven `InstockProductTile` pattern.
 *
 * Cylindo image-quality mitigations:
 *   - `tileIndex` controls eager-loading: only the first `RANGE_PLP_EAGER_TILE_COUNT` tiles set
 *     their hero image to `loading="eager"`; the rest stay `lazy` and load as the user scrolls.
 *     This spreads the cold-request burst over time so Cylindo's render queue isn't slammed at
 *     first paint.
 *   - Cylindo URLs are routed through `buildDynamicImageSrc`, which appends `size=` to force
 *     full-resolution renders. Without it Cylindo can return a low-res cold-render stub
 *     (~512×246) that the browser treats as a successful 200 but visually renders as a thin
 *     strip in our portrait tile. See `responsive-image-src.js` for full rationale.
 *   - Swatch chips use `<img>` (not CSS `background-image`) so a 404/broken Cylindo URL can
 *     `onError` and drop that chip; the tile then probes the next catalog covers until 7 load
 *     (6 visible + overflow sentinel) or the probe cap. The row unmounts only when none of those remain valid.
 *   - When a product's search hit carries no usable image data (`primaryImage` is null), the
 *     tile renders the brand-styled `<ImageNotAvailable>` placeholder instead of a broken
 *     `<img>` element.
 */
import React, {useEffect, useId, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import msg from '@salesforce/retail-react-app/app/components/display-price/messages';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  Badge,
  Box,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {getPriceData, getProductPromoCalloutMessages} from '@amblique/king-living/app/utils/product-utils';
import {productUrlBuilder} from '@salesforce/retail-react-app/app/utils/url';

import BazaarvoiceInlineRating from '@amblique/king-living/app/components/bazaarvoice/inline-rating';
import ImageNotAvailable from '@amblique/king-living/app/components/image-not-available';
import ProductTileImageGallery from '@amblique/king-living/app/components/product-tile-image-gallery';
import {RANGE_PLP_EAGER_TILE_COUNT} from '@amblique/king-living/app/constants';
import {
  getProductTileImageBehaviorClassName,
  getProductTileImageMode,
} from '@amblique/king-living/app/constants/product-tile-image-behavior';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {getMasterTileImages} from '@amblique/king-living/app/utils/product-images';
import {
  getCoverCount,
  getCoverSwatches,
} from '@amblique/king-living/app/utils/product-cover-swatches';
import {
  buildDynamicImageSrc,
  buildSwatchSrc,
} from '@amblique/king-living/app/utils/responsive-image-src';
import {stripHtmlToPlainText} from '@amblique/king-living/app/utils/utils';

/**
 * Tile-grid layout (from `LazyLoadGrid` `columns={[2, 2, 3, 4]}`):
 *   - base / sm: 2 columns → tile ≈ 50vw
 *   - md:        3 columns → tile ≈ 33vw    (use 360px to keep srcSet integers)
 *   - lg / xl:   4 columns → tile ≈ 25vw    (use 360px — equals 1440 viewport ÷ 4)
 *
 * Using fixed px values for non-mobile breakpoints avoids fractional `?sw=327.36…` srcSet entries
 * that some CDNs (Cylindo's `size=`) reject. Mobile keeps `vw` because viewport varies widely.
 */
const TILE_IMAGE_WIDTHS = {
  base: '50vw',
  md: 360,
  lg: 360,
  xl: 400,
};

/**
 * Per-breakpoint swatch caps (FSD design review):
 *   mobile (< md):  3 visible, "+ more" if total > 3
 *   desktop (≥ md): 6 visible, "+ more" if total > 6
 *
 * Implementation note: we resolve up to `desktop + 1` so a hidden 7th chip can prove overflow.
 * Caps and "+ more" follow chips that *loaded* — a constructed Cylindo URL that 404s must not
 * count, and "+ more" must not appear beside empty boxes (PLP-09 / DE-312).
 * 404s widen the candidate window (img onLoad is the 200 check — Cylindo blocks fetch CORS).
 */
const SWATCH_CAP_MOBILE = 3;
const SWATCH_CAP_DESKTOP = 6;
// ponytail: stop probing so a 400-cover master of 404s does not hammer Cylindo on every PLP tile.
const SWATCH_PROBE_MAX = 40;
const EMPTY_SWATCH_KEY_SET = new Set();

const coverSwatchKey = (swatch, index) => String(swatch?.value ?? swatch?.name ?? index);

/**
 * "New" badge visibility window — show when `c_newArrivalDate` falls on today or any of the prior
 * 30 calendar days (31-day inclusive window: day 0 = today, day 30 = 30 days ago).
 *
 * Compares local calendar dates, not `Date.now() - ts <= 30 * 24h`. A pure millisecond window
 * drops products whose arrival date is exactly 30 days ago once the current clock passes the
 * arrival timestamp (e.g. 19 Apr → 19 May fails in the afternoon even though FSD expects "New").
 *
 * Future-dated arrivals are excluded (pre-launch teasers, not "recently landed").
 */
const NEW_ARRIVAL_MAX_DAYS_AGO = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfLocalCalendarDay = (dateInput) => {
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return NaN;

  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

const isWithinNewArrivalWindow = (newArrivalDate) => {
  if (!newArrivalDate) return false;

  const arrivalDay = startOfLocalCalendarDay(newArrivalDate);
  if (Number.isNaN(arrivalDay)) return false;

  const todayDay = startOfLocalCalendarDay(new Date());
  if (arrivalDay > todayDay) return false;

  const daysAgo = Math.floor((todayDay - arrivalDay) / MS_PER_DAY);

  return daysAgo <= NEW_ARRIVAL_MAX_DAYS_AGO;
};

/** Selector matching the in-tile Swiper chrome — clicks on these must NOT trigger PDP navigation. */
const INNER_TILE_SWIPER_CHROME =
  '.swiper-button-next, .swiper-button-prev, .swiper-pagination, .swiper-scrollbar, .swiper-pagination-progressbar, .swiper-pagination-bullet';

const isInnerTileSwiperChrome = (target) => Boolean(target?.closest?.(INNER_TILE_SWIPER_CHROME));

/**
 * SFCC sometimes auto-populates `image.alt` with the asset's URL path fragment (e.g.
 * `J2-PKG 13/frames/8/J2-PKG 13.webp?background=FFFFFF`). That's useless for screen readers and
 * looks like garbage when an image fails to load and the browser falls back to alt text. Treat any
 * alt that contains a path separator or an extension as missing.
 */
const isUsableAlt = (alt) => {
  if (!alt || typeof alt !== 'string') return false;
  const trimmed = alt.trim();

  if (!trimmed) return false;
  if (trimmed.includes('/')) return false;
  if (/\.(webp|jpe?g|png|gif|avif)\b/i.test(trimmed)) return false;

  return true;
};

const RangeProductTile = ({
  product,
  tileIndex = 0,
  variant = 'default',
  showBazaarvoiceRating = false,
  showPromoCallout = false,
  onClick,
  desktopTileMaxWidthPx,
  isInCarouselSlide = false,
  staticHeroImage = false,
  ...rest
}) => {
  const titleId = useId();
  const styles = useMultiStyleConfig('RangeProductTile', {variant});
  const isMobileViewport = useBreakpointValue({base: true, md: false});
  const intl = useIntl();
  const {currency} = useCurrency();
  const navigate = useNavigation();
  // BV tile rating is aria-hidden (nested in Link); surface count in the link label.
  const [reviewCount, setReviewCount] = useState(null);
  // Cover chips that actually loaded vs 404'd. CSS `background-image` cannot report failures, so
  // we render `<img>` and only keep chips (and "+ more") that succeed — DE-312 / PLP-09.
  const [loadedSwatchKeys, setLoadedSwatchKeys] = useState(EMPTY_SWATCH_KEY_SET);
  const [failedSwatchKeys, setFailedSwatchKeys] = useState(EMPTY_SWATCH_KEY_SET);
  const [swatchLimit, setSwatchLimit] = useState(SWATCH_CAP_DESKTOP);

  const productId = product?.productId ?? product?.id;
  const title = product?.productName ?? product?.name ?? '';

  useEffect(() => {
    setReviewCount(null);
    setLoadedSwatchKeys(EMPTY_SWATCH_KEY_SET);
    setFailedSwatchKeys(EMPTY_SWATCH_KEY_SET);
    setSwatchLimit(SWATCH_CAP_DESKTOP);
  }, [productId]);
  // SCAPI productSearch often nests shortDescription on `representedProduct`, not the hit root
  // (same pattern as `c_newArrivalDate` below). getProducts / upsell feeds keep it top-level.
  const shortDescription = useMemo(() => {
    const raw =
      product?.shortDescription ?? product?.representedProduct?.shortDescription ?? '';

    return stripHtmlToPlainText(raw);
  }, [product?.shortDescription, product?.representedProduct?.shortDescription]);
  // Pass the whole product so the king-living `productUrlBuilder` override can pick up
  // `c_productPageURL` (Contentful slug). Falls back to `/product/{id}` when no slug is set.
  const productUrl = productId ? productUrlBuilder(product) : '';

  // Image source-of-truth aligned with PDP `ImageGallery`: PIM photos from `c_processedImageGroups`
  // win over Cylindo URLs surfaced by SCAPI's default `imageGroups`. See `utils/product-images.js`
  // for the full priority and rationale.
  const images = useMemo(() => getMasterTileImages(product), [product]);
  const imageBehaviorClassName = getProductTileImageBehaviorClassName(
    getProductTileImageMode(images.length, {
      isMobile: isMobileViewport,
      isInCarouselSlide,
      staticHeroOnly: staticHeroImage,
    })
  );

  // Search hits always carry a top-level `product.image` (the master tile asset). Use it as the
  // ultimate fallback so even products whose `imageGroups` are empty or unpublished still render.
  const primaryImage = useMemo(() => {
    if (images[0]) return images[0];
    if (product?.image && (product.image.disBaseLink || product.image.link)) {
      return product.image;
    }

    return null;
  }, [images, product?.image]);

  // `getCoverSwatches` returns *displayable* URLs only (no network). Pass `swatchLimit` so we
  // resolve a small window instead of 400+ covers. `img` onLoad/onError is the 200 check; 404s
  // widen the window until the visible cap is filled or `SWATCH_PROBE_MAX`.
  const swatches = useMemo(
    () => getCoverSwatches(product, {limit: swatchLimit}),
    [product, swatchLimit]
  );
  const coverCount = getCoverCount(product);
  const totalSwatches = swatches.length;
  // `limit=n` returns at most n+1. Keep the extra as a load-only sentinel: never painted, but
  // if it loads then `loadedSwatchCount > SWATCH_CAP_DESKTOP` and desktop "+ more" is justified.
  const renderedSwatches = swatches;
  const loadedSwatchOrder = useMemo(() => {
    const order = new Map();
    let index = 0;

    renderedSwatches.forEach((swatch, i) => {
      const key = coverSwatchKey(swatch, i);

      if (loadedSwatchKeys.has(key)) {
        order.set(key, index);
        index += 1;
      }
    });

    return order;
  }, [renderedSwatches, loadedSwatchKeys]);
  const loadedSwatchCount = loadedSwatchOrder.size;
  const swatchBatchSettled =
    renderedSwatches.length > 0 &&
    renderedSwatches.every((swatch, i) => {
      const key = coverSwatchKey(swatch, i);

      return loadedSwatchKeys.has(key) || failedSwatchKeys.has(key);
    });
  const moreSwatchCandidates =
    swatches.length >= swatchLimit + 1 && swatchLimit < SWATCH_PROBE_MAX;
  // Hide the row only when every probed URL 404'd and there is nothing left to try.
  const allProbedFailed =
    swatchBatchSettled && loadedSwatchCount === 0 && !moreSwatchCandidates;

  useEffect(() => {
    if (!swatchBatchSettled) return;
    if (loadedSwatchCount > SWATCH_CAP_DESKTOP) return;
    if (!moreSwatchCandidates) return;

    setSwatchLimit((n) => Math.min(SWATCH_PROBE_MAX, n + SWATCH_CAP_DESKTOP));
  }, [swatchBatchSettled, loadedSwatchCount, moreSwatchCandidates]);

  // Overflow is loaded chips vs cap — not coverCount and not "we found another URL". A 7th URL
  // that 404s must not keep "+ more" next to two empty boxes (Myco / DE-312).
  const hasMobileOverflow = loadedSwatchCount > SWATCH_CAP_MOBILE;
  const hasDesktopOverflow = loadedSwatchCount > SWATCH_CAP_DESKTOP;

  const markSwatchLoaded = (key) => {
    setLoadedSwatchKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);

      return next;
    });
  };
  const markSwatchFailed = (key) => {
    setFailedSwatchKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);

      return next;
    });
  };

  const priceData = useMemo(() => getPriceData(product), [product]);
  const promoCalloutMessages = useMemo(() => {
    if (!showPromoCallout) return [];

    return getProductPromoCalloutMessages(product);
  }, [product, showPromoCallout]);
  const productCurrency = product?.currency || currency;
  const isOnSale = Boolean(priceData?.isOnSale);
  const showFromPrefix = Boolean(priceData?.isRange);
  // Sale + New are independent — both can appear on the same tile (rendered as a vertical stack).
  //
  // SCAPI `productSearch` nests product-level custom attributes inside `representedProduct` (the
  // canonical variant the search index resolved to), not on the hit itself. `getProduct` on the
  // PDP path returns `c_newArrivalDate` at the top level instead. Read both so this works whether
  // the hook is fed a search hit or a full product detail object — which also keeps unit tests
  // and Storybook fixtures simple.
  const newArrivalDate =
    product?.c_newArrivalDate ?? product?.representedProduct?.c_newArrivalDate;
  const isNew = isWithinNewArrivalWindow(newArrivalDate);

  const formattedCurrent = useMemo(() => {
    if (priceData?.currentPrice == null) return '';

    return formatCurrencyHideTrailingZeroCents(intl, priceData.currentPrice, productCurrency);
  }, [intl, priceData?.currentPrice, productCurrency]);

  const formattedList = useMemo(() => {
    if (priceData?.listPrice == null) return '';

    return formatCurrencyHideTrailingZeroCents(intl, priceData.listPrice, productCurrency);
  }, [intl, priceData?.listPrice, productCurrency]);

  const heroAspectRatioProps =
    desktopTileMaxWidthPx != null ?
      {
        ...styles.aspectRatio,
        maxW: {base: '100%', lg: `${desktopTileMaxWidthPx}px`},
      } :
      styles.aspectRatio;

  const handleImageAreaClick = (e) => {
    if (!productUrl) return;
    if (isInnerTileSwiperChrome(e.target)) {
      // Carousel chrome (arrows / scrollbar): swallow the bubbled click so only the Swiper handles it.
      e.stopPropagation();

      return;
    }
    e.preventDefault();
    e.stopPropagation();

    if (typeof onClick === 'function') onClick(product);

    navigate(productUrl);
  };

  // Cylindo cold-render mitigation: only the first `RANGE_PLP_EAGER_TILE_COUNT` tiles in the grid
  // mark their hero image as `loading="eager"`. Tiles past that threshold stay `lazy` so the
  // browser only requests them once they scroll into view, which spreads cold Cylindo requests
  // over time instead of firing them in a synchronous burst at first paint.
  const isInEagerWindow = tileIndex < RANGE_PLP_EAGER_TILE_COUNT;

  // The placeholder branch only fires when the search hit carries no usable image data at all.
  // For genuine in-flight load failures we rely on the browser's own broken-image affordance —
  // `buildDynamicImageSrc`'s `size=` injection (in `responsive-image-src.js`) keeps Cylindo from
  // returning low-res cold-render stubs in the first place, so this surfaces only true Mode-B
  // (bad URL data) cases for ops to fix in PIM/BM.
  const showImagePlaceholder = !primaryImage;

  const tileImages = images.length > 0 ? images : primaryImage ? [primaryImage] : [];

  const imageContent = (
    <ProductTileImageGallery
      aspectRatioProps={heroAspectRatioProps}
      buildSrc={(img) => buildDynamicImageSrc(img.disBaseLink || img.link)}
      getAlt={(img, i) => {
        if (isUsableAlt(img.alt)) return img.alt;

        return tileImages.length === 1 ? title : `${title} image ${i + 1}`;
      }}
      getLoading={(i) => (i === 0 && isInEagerWindow ? 'eager' : 'lazy')}
      hoverSwapLayerProps={styles.hoverSwapLayer}
      hoverSwapPrimaryProps={styles.hoverSwapPrimary}
      hoverSwapSecondaryProps={styles.hoverSwapSecondary}
      hoverSwapWrapperProps={styles.hoverSwapWrapper}
      imageWidths={TILE_IMAGE_WIDTHS}
      images={showImagePlaceholder ? [] : tileImages}
      isInCarouselSlide={isInCarouselSlide}
      staticHeroImage={staticHeroImage}
      placeholder={
        <ImageNotAvailable
          svgProps={styles.placeholderImage}
          wrapperProps={styles.placeholderWrapper}
        />
      }
      swiperContainerProps={styles.swiperContainer}
      swiperPreset="range_tile"
    />
  );

  // PLP-09: if no valid cover imagery, do not render the overlay (no empty boxes, no "+ more").
  const swatchesRow = totalSwatches > 0 && !allProbedFailed && (
    <Box
      data-testid="range-product-tile-swatches"
      role="presentation"
      className="range-product-tile-swatches"
      aria-hidden={loadedSwatchCount === 0 ? true : undefined}
      aria-label={
        loadedSwatchCount === 0 ?
          undefined :
          intl.formatMessage(
            {
              defaultMessage: 'Available in {count, plural, one {# cover} other {# covers}}',
              id: 'range_product_tile.swatches.available',
            },
            {count: coverCount || totalSwatches}
          )
      }
      {...styles.swatchesRow}
    >
      {renderedSwatches.map((s, i) => {
        // `getCoverSwatches` omits covers without `pimlyProductImageUrl`. Broken PIM URLs still
        // 404 — `<img onError>` drops that chip. `buildSwatchSrc` normalises encoding and sizes
        // the proxy URL for the ~12px chip.
        const key = coverSwatchKey(s, i);

        if (failedSwatchKeys.has(key)) return null;

        const swatchUrl = buildSwatchSrc(s.swatchUrl);
        const loadedIndex = loadedSwatchOrder.get(key);
        const isLoaded = loadedIndex != null;
        // Always pass a real `display` value. `display={undefined}` after `{...styles.swatchItem}`
        // still overrides the theme's `display: flex` (span falls back to inline) — that's why
        // chips 1–3 ignored the theme while chips 4+ (`{base:'none', md:'flex'}`) looked fine.
        // 7th (index >= desktop cap) is the overflow sentinel — load it, never paint it.
        const breakpointDisplay = !isLoaded || loadedIndex >= SWATCH_CAP_DESKTOP ?
          'none' :
          loadedIndex >= SWATCH_CAP_MOBILE ? {base: 'none', md: 'flex'} : 'flex';

        return (
          <Box
            key={s.value ?? s.name ?? i}
            as="span"
            title={s.name}
            {...styles.swatchItem}
            display={breakpointDisplay}
          >
            <Box
              as="img"
              alt=""
              aria-hidden
              className="range-product-tile-swatch-image"
              onError={() => markSwatchFailed(key)}
              onLoad={() => markSwatchLoaded(key)}
              src={swatchUrl}
              {...styles.swatchItemImage}
            />
          </Box>
        );
      })}

      {hasMobileOverflow && (
        // Visible on mobile when *loaded* count > 3; on desktop when loaded count > 6.
        <Text
          as="span"
          {...styles.swatchMore}
          display={{
            base: hasMobileOverflow ? 'inline' : 'none',
            md: hasDesktopOverflow ? 'inline' : 'none',
          }}
        >
          <FormattedMessage
            defaultMessage="+ more"
            id="range_product_tile.swatches.more"
          />
        </Text>
      )}
    </Box>
  );

  // Figma PLP tile (21051:15340): title → price → BV stars → promo callout.
  const priceBlock = priceData?.currentPrice != null && (
    <Box {...styles.priceSection}>
      <Box {...styles.pricePrimaryRow}>
        <Text
          as="span"
          className={[
            'range-product-tile__price-current',
            isOnSale ? 'range-product-tile__price-current--on-sale' : '',
          ].filter(Boolean).join(' ')}
          {...styles.priceCurrent}
          color={isOnSale ? 'secondary.newSaleRed' : styles.priceCurrent.color}
          aria-label={intl.formatMessage(msg.ariaLabelCurrentPrice, {
            currentPrice: formattedCurrent,
          })}
        >
          {isOnSale ? (
            <FormattedMessage
              defaultMessage="Sale price from"
              id="range_product_tile.price.sale_price_from"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Full price from"
              id="range_product_tile.price.full_price_from"
            />
          )}{' '}
          {formattedCurrent}
        </Text>

        {isOnSale && formattedList && (
          <Box {...styles.priceFullRow}>
            <Text as="span" {...styles.priceFullLabel}>
              <FormattedMessage
                defaultMessage="Full price from"
                id="range_product_tile.price.full_price_from"
              />
            </Text>
            <Text
              as="span"
              {...styles.priceFullStrike}
              aria-label={intl.formatMessage(msg.ariaLabelListPrice, {
                listPrice: formattedList,
              })}
            >
              {formattedList}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );

  const promoCallouts = promoCalloutMessages.map((calloutHtml, index) => (
    <Box
      key={`${productId}-promo-${index}`}
      dangerouslySetInnerHTML={{__html: calloutHtml}}
      data-testid={
        index === 0 ?
          'range-product-tile-promo-callout' :
          `range-product-tile-promo-callout-${index}`
      }
      {...styles.promoCallout}
    />
  ));

  // Title + price + BV (+ promo below BV) wrapped in one Link so the whole info block
  // navigates to PDP. Image-area click is separate so carousel arrows can preempt.
  // BV is display-only (pointer-events none) so it does not nest interactive controls.
  const infoSection = (
    <Box {...styles.infoSection} data-testid="range-product-tile-info">
      <Box className="range-product-tile__copy" {...styles.copyStack}>
        <Text className="range-product-tile__title" id={titleId} {...styles.title}>
          {title}
        </Text>
        {shortDescription && (
          <Text className="range-product-tile__subtitle" {...styles.subtitle}>{shortDescription}</Text>
        )}
      </Box>
      {priceBlock}
      {showBazaarvoiceRating && (
        <BazaarvoiceInlineRating
          key={productId}
          onReviewCountChange={setReviewCount}
          product={product}
          variant="tile"
        />
      )}
      {promoCallouts}
    </Box>
  );

  const linkAriaLabel = (() => {
    const base =
      title ?
        formattedCurrent ?
          intl.formatMessage(
            {
              defaultMessage: '{title}, {price}',
              id: 'range_product_tile.link.aria_label',
            },
            {title, price: formattedCurrent}
          ) :
          title :
        intl.formatMessage({
          defaultMessage: 'View product details',
          id: 'range_product_tile.link.view_generic',
        });

    if (reviewCount == null) {
      return base;
    }

    return intl.formatMessage(
      {
        defaultMessage: '{label}, {count} reviews',
        id: 'range_product_tile.link.aria_label_with_reviews',
      },
      {label: base, count: reviewCount}
    );
  })();

  return (
    <Box
      data-testid={`range-product-tile-${productId}`}
      role="group"
      {...styles.container}
      {...rest}
    >
      <Box
        data-testid="range-product-tile-image"
        {...styles.imageWrapper}
        className={[
          'swiper-range-tile',
          imageBehaviorClassName,
          isInCarouselSlide ? 'product-tile-image--in-carousel-slide' : '',
        ].filter(Boolean).join(' ')}
        cursor={productUrl ? 'pointer' : undefined}
        onClick={productUrl ? handleImageAreaClick : undefined}
      >
        {imageContent}
        {(isOnSale || isNew) && (
          <Box
            data-testid="range-product-tile-badges"
            {...styles.badgeStack}
          >
            {isOnSale && (
              <Badge {...styles.saleBadge} className="kl-product-tile-badge" data-testid="range-product-tile-sale-badge">
                <FormattedMessage
                  defaultMessage="Sale"
                  id="range_product_tile.badge.sale"
                />
              </Badge>
            )}
            {isNew && (
              <Badge {...styles.newBadge} className="kl-product-tile-badge" data-testid="range-product-tile-new-badge">
                <FormattedMessage
                  defaultMessage="New"
                  id="range_product_tile.badge.new"
                />
              </Badge>
            )}
          </Box>
        )}
        {swatchesRow}
      </Box>

      {productUrl ? (
        <Link
          data-testid="range-product-tile-link"
          to={productUrl}
          aria-label={linkAriaLabel}
          onClick={() => {
            if (typeof onClick === 'function') onClick(product);
          }}
          {...styles.link}
        >
          {infoSection}
        </Link>
      ) : (
        infoSection
      )}
    </Box>
  );
};

RangeProductTile.propTypes = {
  product: PropTypes.object.isRequired,
  // Position of this tile in the parent grid. Drives the eager/lazy-loading threshold (only the
  // first `RANGE_PLP_EAGER_TILE_COUNT` tiles eagerly request their hero image). Defaults to 0
  // so standalone usage (e.g. tests, storybook) gets eager loading by default.
  tileIndex: PropTypes.number,
  variant: PropTypes.oneOf(['default', 'upsell']),
  /** PLP only — Bazaarvoice stars and review count below the price */
  showBazaarvoiceRating: PropTypes.bool,
  /** Range PLP lazy grid only — promo callout under BV / price (Figma 21051:15340) */
  showPromoCallout: PropTypes.bool,
  onClick: PropTypes.func,
  /** CF Product Tiles: caps hero frame width at lg (424 vs 317); omit on Range PLP grid */
  desktopTileMaxWidthPx: PropTypes.number,
  /** Contentful Product Tiles outer carousel — show in-tile swiper arrows */
  isInCarouselSlide: PropTypes.bool,
  /** Account landing suggested carousel — static first image only, no inner Swiper */
  staticHeroImage: PropTypes.bool,
};

const MemoRangeProductTile = React.memo(RangeProductTile);

MemoRangeProductTile.displayName = 'RangeProductTile';

export default MemoRangeProductTile;
